import './styles.scss';

import { useState, useEffect, useRef, useCallback } from 'react';

import { ReactComponent as Logo } from '../../assets/logo/LIGHT_LOGO.svg';

import Button from '../button';

import { ReactComponent as LoginIcon } from '../../assets/icons/login.svg';

export default function Header({ containerRef: externalContainerRef }) {
    const headerRef = useRef(null);
    const rafIdRef = useRef(null);
    const [scrollProgress, setScrollProgress] = useState(0);

    // Usa requestAnimationFrame para otimizar as atualizações
    const updateProgress = useCallback(() => {
        const container = externalContainerRef?.current || document.querySelector('.home-page');
        if (!container) return;

        const scrollPosition = container.scrollTop;
        const maxScroll = 400;
        const progress = Math.min(1, scrollPosition / maxScroll);

        // Atualiza apenas se mudou significativamente (evita re-renders desnecessários)
        setScrollProgress(prev => {
            if (Math.abs(prev - progress) > 0.01) {
                return progress;
            }
            return prev;
        });

        rafIdRef.current = null;
    }, [externalContainerRef]);

    // Throttled scroll handler usando requestAnimationFrame
    const handleScroll = useCallback(() => {
        if (rafIdRef.current === null) {
            rafIdRef.current = requestAnimationFrame(updateProgress);
        }
    }, [updateProgress]);

    useEffect(() => {
        const container = externalContainerRef?.current || document.querySelector('.home-page');
        if (!container) return;

        // Inicializa valores
        updateProgress();

        // Adiciona listener
        container.addEventListener('scroll', handleScroll, { passive: true });

        return () => {
            container.removeEventListener('scroll', handleScroll);
            if (rafIdRef.current !== null) {
                cancelAnimationFrame(rafIdRef.current);
            }
        };
    }, [handleScroll, updateProgress, externalContainerRef]);

    // Calcula a largura dos pseudo-elementos baseado no progresso
    const initialWidth = 18;
    const finalWidth = 100;
    const currentWidth = initialWidth + (finalWidth - initialWidth) * scrollProgress;

    // Handlers para scroll suave até os elementos
    const scrollToSection = useCallback((sectionId) => {
        const container = externalContainerRef?.current || document.querySelector('.home-page');
        if (!container) return;

        const section = document.getElementById(sectionId);
        if (!section) return;

        const containerRect = container.getBoundingClientRect();
        const sectionRect = section.getBoundingClientRect();
        
        // Calcula a posição relativa ao container
        const scrollTop = container.scrollTop;
        const sectionTop = sectionRect.top - containerRect.top + scrollTop;

        // Faz scroll suave
        container.scrollTo({
            top: sectionTop - 20, // Offset de 20px para não ficar colado no topo
            behavior: 'smooth'
        });
    }, [externalContainerRef]);

    const handleScrollToBenefits = useCallback(() => {
        scrollToSection('benefits-section');
    }, [scrollToSection]);

    const handleScrollToFaq = useCallback(() => {
        scrollToSection('faq-section');
    }, [scrollToSection]);

    const handleScrollToPricing = useCallback(() => {
        scrollToSection('pricing-section');
    }, [scrollToSection]);

    return (
        <header
            ref={headerRef}
            style={{
                '--before-width': `${currentWidth}%`,
                '--after-width': `${currentWidth}%`
            }}
        >
            <div className="content">
                <Logo className="logo" />

                <div className='main-info'>
                    <span className='main-info-text pressable' onClick={handleScrollToBenefits}>funcionalidades</span>
                    <span className='main-info-text pressable' onClick={handleScrollToFaq}>dúvidas</span>
                    <span className='main-info-text pressable' onClick={handleScrollToPricing}>preço</span>
                    <span className='main-info-text pressable' onClick={() => {
                        if (window.config?.redirects?.openHelpDesk) {
                            window.config.redirects.openHelpDesk();
                        }
                    }}>suporte</span>
                </div>

                <Button.Medium
                    text="Entrar"
                    onClick={() => {
                        if (window.config?.redirects?.openApp) {
                            window.config.redirects.openApp();
                        }
                    }}
                    rightIcon={<LoginIcon />}
                />
            </div>
        </header>
    );
}